






























































import ApiConfiguration from '@/classes/ApiConfiguration';
import SnotifyConfiguration from '@/classes/SnotifyConfiguration';
import { BackgroundDocumentFailureModel, BackgroundExportRequestDataModel, BulkPrintRequestStatus, SearchResultsDocumentModel } from '@/services/GeneratedApi';
import { Base64Utils } from '@/utils';
import Vue, { PropType } from 'vue';
import LoadingSpinner from '../shared/LoadingSpinner.vue';

export default Vue.extend({
  props: {
    documents: {
      type: Array as PropType<Array<Pick<SearchResultsDocumentModel, 'cabinetId' | 'documentId' | 'pages'>>>,
      required: true
    },
    includeRelatedDocuments: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isPrintModalOpen: false,
      isLoading: false,
      isExporting: false,
      requiresBackgroundPrint: false,
      unableToPrintMessage: '',
      documentsForBackgroundPrint: [],
      documentsUnableToPrint: [] as BackgroundDocumentFailureModel[],
      documentBase64: ''
    };
  },
  computed: {
    totalPageCount(this: any) {
      return (this.documents as Array<Pick<SearchResultsDocumentModel, 'cabinetId' | 'documentId' | 'pages' | 'documentTypeId'>>).reduce((total, { pages }) => total + pages!, 0);
    },
    portfolioPdfDocuments(): BackgroundDocumentFailureModel[] {
      return (this.documentsUnableToPrint as BackgroundDocumentFailureModel[]).filter((doc) => doc.isPortfolioPDF);
    },
    hasPrintableDocuments(): boolean {
      return !!this.documentBase64;
    }
  },
  methods: {
    async onPrintClick(this: any) {
      this.isLoading = true;
      this.isPrintModalOpen = true;
      try {
        const response = await ApiConfiguration.api().documentPrint.documentPrintBulkprintCreate({ documents: this.documents, includeRelatedDocuments: this.includeRelatedDocuments });
        if (response.status === BulkPrintRequestStatus.Background) {
          this.requiresBackgroundPrint = true;
          this.documentsForBackgroundPrint = response.documentsToPrint;
          this.documentsUnableToPrint = response.documentsNotPrinted;
        } else if (response.status === BulkPrintRequestStatus.UnableToPrint) {
          this.unableToPrintMessage = response.message;
          this.documentsUnableToPrint = response.documentsNotPrinted;
        } else {
          this.documentBase64 = response.documentBase64;
          if (response.documentsNotPrinted?.length) {
            this.documentsUnableToPrint = response.documentsNotPrinted;
            return;
          }
          await Vue.nextTick();
          this.showPrintDialog();
          this.isPrintModalOpen = false;
        }
      } finally {
        this.isLoading = false;
      }
    },
    async onBackgroundPrint(this: any) {
      this.isLoading = true;
      try {
        const response = await ApiConfiguration.api().documentPrint.documentPrintBackgroundprintCreate({ documents: this.documentsForBackgroundPrint });
        SnotifyConfiguration.showSuccess(response.endUserMessage || 'Successfully queued documents for print');
      } finally {
        this.isLoading = false;
        this.isPrintModalOpen = false;
      }
    },
    onContinueToPrint(this: any) {
      this.showPrintDialog();
      this.isPrintModalOpen = false;
    },
    async onExportPortfolioPdfs(this: any) {
      this.isExporting = true;
      try {
        const exportRequest: BackgroundExportRequestDataModel = {
          documents: this.portfolioPdfDocuments.map(({ cabinetId, documentId, documentTypeId }: BackgroundDocumentFailureModel) => ({
            cabinetId,
            documentId,
            documentTypeId
          })),
          includeRelatedDocuments: this.includeRelatedDocuments
        };
        const result = await ApiConfiguration.api().documentExport.documentExportQueuebackgroundexportCreate(exportRequest);
        SnotifyConfiguration.showSuccess(result.endUserMessage ?? 'Export queued successfully.');
        if (!this.hasPrintableDocuments) {
          this.isPrintModalOpen = false;
        }
      } catch (error) {
        SnotifyConfiguration.showError('Failed to queue export.');
      } finally {
        this.isExporting = false;
      }
    },
    showPrintDialog(this: any) {
      if (!this.documentBase64) {
        return;
      }
      const fileUrl = Base64Utils.previewBase64(this.documentBase64!, null);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.style.visibility = 'hidden';
      iframe.src = fileUrl;

      iframe.onload = () => {
        iframe.contentWindow?.print();
        URL.revokeObjectURL(fileUrl);
      };

      document.body.appendChild(iframe);
    },
    onCancel(this: any, cancel: () => void) {
      cancel();
    },
    onHidden(this: any) {
      this.requiresBackgroundPrint = false;
      this.unableToPrintMessage = '';
      this.documentsForBackgroundPrint = [];
      this.documentsUnableToPrint = [];
      this.documentBase64 = '';
      this.isExporting = false;
    }
  },
  components: {
    LoadingSpinner
  }
});
